/**
 * Gate 3 — Entry Check.
 * Passed prerequisites use the green summary. Missing monitoring signals or a
 * deployment plan use the attention layout with fix-at-source and approval actions.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Box, Button, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import { ReactComponent as GreenFilledTickMarkIcon } from "../../asserts/logo/GreenFilledTickMarkIcon.svg";
import { ReactComponent as SuccessWarningIcon } from "../../asserts/logo/SuccessWarning.svg";
import { ReactComponent as WarningTriangleIcon } from "../../asserts/logo/WarningTraingleIcon.svg";
import AppToast from "../../components/feedback/AppToast";
import { API_BASE_URL } from "../../config";
import TruncatedCellLine from "../../components/TruncatedCellLine";
import Gate3ReadinessChecklist, { initialChecklistValues, isSupportedAgenticArchetype } from "./Gate3ReadinessChecklist";
import Gate3FlagEvaluation from "./Gate3FlagEvaluation";
import Gate3Decision from "./Gate3Decision";
import Gate3Outputs from "./Gate3Outputs";
import IntakeGateTabsBar from "../../components/intake/IntakeGateTabsBar";
import { deploymentPlanHref } from "../../utils/validateApiBaseUrl";
import StickyPageHeaderWithBack from "../../components/layout/StickyPageHeaderWithBack";
import {
  PRIMARY_BTN_SX,
  SCOPE_CANCEL_BTN_SX,
  STEP_RAIL_COMPLETED_CHIP_SX,
  STEP_RAIL_IN_PROGRESS_CHIP_SX,
  STEP_RAIL_LABEL_DEFAULT_SX,
  STEP_RAIL_LABEL_IN_PROGRESS_SX,
  STEP_RAIL_PLANNED_CHIP_SX,
  STEP_RAIL_ROW_ACTIVE_SX,
  STEP_RAIL_ROW_BASE_SX,
  STEP_RAIL_ROW_PLANNED_SX,
} from "../gate2/gate2Tokens";

const ACTIVE_GATE = 3;
const GATE3_VOTES = new Set(["go", "conditional_go", "no_go"]);

function emptyChecklistFiles() {
  return { incident: null, rollback: null };
}

function checklistFilesFromGate3(gate3) {
  const files = emptyChecklistFiles();
  if (!gate3?.checklistFiles || typeof gate3.checklistFiles !== "object") return files;
  ["incident", "rollback"].forEach((key) => {
    const file = gate3.checklistFiles[key];
    if (file && typeof file.name === "string" && file.name.trim() && typeof file.content === "string" && file.content) {
      files[key] = { name: file.name, content: file.content };
    }
  });
  return files;
}

function checklistValuesFromGate3(gate3) {
  const next = { ...initialChecklistValues() };
  if (!gate3?.checklist || typeof gate3.checklist !== "object") return next;
  Object.entries(gate3.checklist).forEach(([key, value]) => {
    if (typeof value === "string") next[key] = value;
  });
  return next;
}

/** Replace the on-screen Gate 3 form. Missing fields stay empty so another project's answers cannot remain. */
function restoreGate3(gate3, apply) {
  if (!gate3 || typeof gate3 !== "object") return;
  const signalName = typeof gate3.signalName === "string" ? gate3.signalName : "";
  const planLink = typeof gate3.planLink === "string" ? gate3.planLink : "";
  apply.setSignalName(signalName);
  apply.setPlanLink(planLink);
  if (typeof gate3.savedSignal === "string") apply.setSavedSignal(gate3.savedSignal.trim());
  else apply.setSavedSignal(signalName.trim());
  if (typeof gate3.savedPlan === "string") apply.setSavedPlan(gate3.savedPlan.trim());
  else apply.setSavedPlan(planLink.trim());
  apply.setChecklistValues(checklistValuesFromGate3(gate3));
  apply.setChecklistCustomDays(typeof gate3.checklistCustomDays === "string" ? gate3.checklistCustomDays : "0");
  apply.setChecklistFiles(checklistFilesFromGate3(gate3));
  apply.setDecisionVote(GATE3_VOTES.has(gate3.decisionVote) ? gate3.decisionVote : "conditional_go");
  apply.setDecisionRationale(typeof gate3.decisionRationale === "string" ? gate3.decisionRationale : "");
  apply.setDecisionAttested(gate3.decisionAttested === true);
  apply.setDecisionException({
    condition: typeof gate3.decisionCondition === "string" ? gate3.decisionCondition : "",
    control: typeof gate3.decisionControl === "string" ? gate3.decisionControl : "",
    expiry: typeof gate3.decisionExpiry === "string" ? gate3.decisionExpiry : "",
  });
  apply.setDecisionUpdated(typeof gate3.decisionUpdated === "string" ? gate3.decisionUpdated : "");
}

const SIGNAL_NAME_MIN = 3;
const SIGNAL_NAME_MAX = 100;
const SIGNAL_NAME_LIMIT_MSG = "Character limit exceeded";

/** Letters, numbers, spaces, and the placeholder punctuation (comma, hyphen, period). Other symbols are dropped. */
function sanitizeSignalName(value) {
  return String(value || "").replace(/[^\p{L}\p{N}\s,.\-]/gu, "");
}

function isValidSignalName(value) {
  const name = String(value || "").trim();
  return name.length >= SIGNAL_NAME_MIN && name.length <= SIGNAL_NAME_MAX && /\p{L}/u.test(name);
}

/** Deployment plan links must be https URLs with a real host, not plain text or a number. */
function isValidPlanLink(value) {
  const link = String(value || "").trim();
  if (!/^https:\/\//i.test(link) || /\s/.test(link)) return false;
  try {
    const url = new URL(link);
    const host = url.hostname;
    if (url.protocol !== "https:" || !host) return false;
    if (host.toLowerCase() === "localhost") return true;
    return host.includes(".") && !host.startsWith(".") && !host.endsWith(".");
  } catch {
    return false;
  }
}

const STEPS = [
  { id: "entry", label: "Entry Check" },
  { id: "checklist", label: "Readiness Checklist" },
  { id: "flags", label: "Flag Evaluation" },
  { id: "decision", label: "Decision" },
  { id: "outputs", label: "Release Summary" },
];

const FONT = "Lato, sans-serif";

/** Desktop (lg) keeps the Figma sizes. Smaller breakpoints step type, controls, and spacing down. */
const TYPE_TITLE = { fontSize: { xs: 14, sm: 15, lg: 16 }, lineHeight: { xs: 1.25, lg: 1 } };
const TYPE_DETAIL = { fontSize: { xs: 12, sm: 13, lg: 14 }, lineHeight: { xs: 1.35, lg: 1 } };
const TYPE_OPTION = { fontSize: { xs: 13, sm: 13, lg: 14 }, lineHeight: { xs: 1.3, lg: 1 } };
const TYPE_ALERT = { fontSize: { xs: 12, sm: 13, lg: 14 }, lineHeight: { xs: "18px", sm: "20px", lg: "21px" } };
const TYPE_FIELD = { fontSize: { xs: 13, lg: 14 }, lineHeight: { xs: "18px", lg: "21px" } };
const TYPE_BUTTON = { fontSize: { xs: 13, sm: 14, lg: 15 }, lineHeight: { xs: "20px", sm: "22px", lg: "24px" } };
const TYPE_PLACEHOLDER = { fontSize: { xs: 12, lg: 13 }, lineHeight: { xs: "18px", lg: "24px" } };

const ALERT_SHELL_SX = {
  display: "flex",
  alignItems: { xs: "flex-start", lg: "center" },
  gap: { xs: "10px", sm: "12px", lg: "16px" },
  width: "100%",
  maxWidth: "100%",
  minWidth: 0,
  boxSizing: "border-box",
  p: { xs: "12px", sm: "14px", lg: "16px" },
  borderRadius: { xs: "6px", lg: "8px" },
  border: "none",
  overflow: "hidden",
};

const OPTION_ROW_SX = {
  display: "flex",
  flexDirection: { xs: "column", sm: "row" },
  alignItems: { xs: "stretch", sm: "center" },
  justifyContent: "space-between",
  gap: { xs: 1.5, sm: 2 },
  border: "1px solid #E6E6E6",
  borderRadius: { xs: "6px", lg: "8px" },
  px: { xs: 1.5, lg: 2 },
  py: { xs: 1.25, lg: 1.5 },
};

const FIELD_ROW_SX = {
  display: "flex",
  flexDirection: { xs: "column", md: "row" },
  alignItems: { xs: "stretch", md: "center" },
  gap: { xs: 1.25, md: 1.5 },
};

function formatDecisionDate(iso) {
  const date = iso ? new Date(iso) : new Date();
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function approvalDetail(vote, decidedAt) {
  const date = formatDecisionDate(decidedAt);
  const suffix = date ? ` · ${date}` : "";
  if (vote === "go") return `Approved${suffix}`;
  if (vote === "no_go") return `No-Go recorded${suffix}`;
  return `Approved with conditions${suffix}`;
}

function StatusMark({ passed }) {
  if (passed) {
    return (
      <Box
        aria-hidden
        sx={{
          width: { xs: 16, sm: 18, lg: 20 },
          height: { xs: 16, sm: 18, lg: 20 },
          flexShrink: 0,
          display: "flex",
          "& svg": { width: "100%", height: "100%", display: "block" },
          "& path": { fill: "#00754E" },
        }}
      >
        <GreenFilledTickMarkIcon />
      </Box>
    );
  }
  return (
    <Box
      aria-hidden
      sx={{
        width: { xs: 18, sm: 20, lg: 22 },
        height: { xs: 18, sm: 20, lg: 22 },
        borderRadius: "50%",
        flexShrink: 0,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "#8A5C00",
        color: "#FFFFFF",
      }}
    >
      <PriorityHighIcon sx={{ fontSize: { xs: 12, lg: 14 } }} />
    </Box>
  );
}

function DeploymentPlanLink({ url }) {
  const href = deploymentPlanHref(url);
  if (!href) return url ? String(url) : null;
  return (
    <Box
      component="a"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      sx={{ color: "#005BF0", fontWeight: 700, textDecoration: "underline", wordBreak: "break-all" }}
    >
      {href}
    </Box>
  );
}

function CheckRow({ passed, title, detail, actions, children }) {
  const compact = !children;
  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "100%",
        boxSizing: "border-box",
        height: "auto",
        minHeight: compact ? { lg: 66 } : 0,
        borderRadius: "4px",
        border: "1px solid #E6E6E6",
        bgcolor: "#FFFFFF",
        pt: { xs: "10px", lg: "12px" },
        pr: { xs: "12px", lg: "16px" },
        pb: { xs: "10px", lg: "12px" },
        pl: { xs: "12px", lg: "16px" },
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        gap: { xs: "12px", lg: "16px" },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: { xs: "10px", lg: "16px" }, minWidth: 0 }}>
        <StatusMark passed={passed} />
        <Box sx={{ minWidth: 0, flex: "1 1 180px", display: "flex", flexDirection: "column", gap: "4px" }}>
          <Typography
            sx={{
              fontFamily: FONT,
              fontWeight: 700,
              fontSize: TYPE_TITLE.fontSize,
              lineHeight: TYPE_TITLE.lineHeight,
              letterSpacing: 0,
              color: "#383838",
            }}
          >
            {title}
          </Typography>
          <Typography
            sx={{
              fontFamily: FONT,
              fontWeight: 400,
              fontSize: TYPE_DETAIL.fontSize,
              lineHeight: TYPE_DETAIL.lineHeight,
              letterSpacing: 0,
              color: "#707070",
            }}
          >
            {detail}
          </Typography>
        </Box>
        {actions}
      </Box>
      {children}
    </Box>
  );
}

function OptionBlock({ label, children }) {
  return (
    <Box sx={{ mt: 2 }}>
      <Typography
        sx={{
          fontFamily: FONT,
          fontWeight: 700,
          fontSize: TYPE_OPTION.fontSize,
          lineHeight: TYPE_OPTION.lineHeight,
          letterSpacing: 0,
          color: "#555555",
          mb: 1,
        }}
      >
        {label}
      </Typography>
      {children}
    </Box>
  );
}

function FieldLabel({ htmlFor, required = true, children }) {
  return (
    <Typography
      component="label"
      htmlFor={htmlFor}
      sx={{
        fontFamily: FONT,
        fontWeight: 700,
        fontSize: TYPE_FIELD.fontSize,
        lineHeight: TYPE_FIELD.lineHeight,
        letterSpacing: 0,
        color: "#707070",
        display: "block",
        mb: 0.75,
      }}
    >
      {required ? (
        <Box component="span" sx={{ color: "#D92D20", mr: 0.25 }} aria-hidden>
          *
        </Box>
      ) : null}
      {children}
    </Typography>
  );
}

const fieldSx = {
  flex: 1,
  minWidth: 0,
  height: { xs: 36, lg: 40 },
  boxSizing: "border-box",
  border: "1px solid #D0D5DD",
  borderRadius: { xs: "4px", lg: "6px" },
  px: { xs: 1.25, lg: 1.5 },
  fontFamily: FONT,
  fontSize: TYPE_DETAIL.fontSize,
  color: "#1A1A1A",
  outline: "none",
  bgcolor: "#FFFFFF",
  "&:focus": { borderColor: "#005BF0" },
};

const grayBtnSx = {
  textTransform: "none",
  fontFamily: FONT,
  fontWeight: 700,
  fontSize: TYPE_BUTTON.fontSize,
  lineHeight: TYPE_BUTTON.lineHeight,
  letterSpacing: 0,
  boxSizing: "border-box",
  width: { xs: "100%", sm: "auto", lg: 118 },
  minWidth: { xs: 0, lg: 118 },
  height: { xs: 36, sm: 38, lg: 40 },
  minHeight: { xs: 36, lg: 40 },
  px: { xs: "12px", lg: "16px" },
  py: { xs: "6px", lg: "8px" },
  gap: "4px",
  borderRadius: "4px",
  border: "1px solid #BBBBBB",
  color: "#555555",
  bgcolor: "#F5F5F5",
  backgroundImage:
    "linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.04) 100%), linear-gradient(0deg, #F5F5F5, #F5F5F5)",
  boxShadow: "0px 1px 2px 0px #00000014",
  whiteSpace: "nowrap",
  flexShrink: 0,
  "& .MuiTouchRipple-root": { display: "none" },
  "&:hover:not(.Mui-disabled):not(:focus):not(:focus-visible):not(.Mui-focusVisible):not(:active)": {
    border: "1px solid #BBBBBB",
    color: "#555555",
    bgcolor: "#F5F5F5",
    backgroundColor: "#F5F5F5",
    backgroundImage:
      "linear-gradient(180deg, rgba(0, 0, 0, 0.04) 0%, rgba(0, 0, 0, 0.08) 100%), linear-gradient(0deg, #F5F5F5, #F5F5F5)",
    boxShadow: "0px 1px 2px 0px #00000014",
  },
  "&:focus:not(:active), &:focus-visible:not(:active), &.Mui-focusVisible:not(:active)": {
    outline: "none",
    color: "#555555",
    bgcolor: "#F5F5F5 !important",
    backgroundColor: "#F5F5F5 !important",
    backgroundImage:
      "linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.04) 100%), linear-gradient(0deg, #F5F5F5, #F5F5F5) !important",
    border: "2px solid #555555",
    boxShadow: "0px 1px 2px 0px #00000014",
  },
  "&:hover:focus:not(:active), &:hover:focus-visible:not(:active), &:hover.Mui-focusVisible:not(:active)": {
    color: "#555555",
    bgcolor: "#F5F5F5 !important",
    backgroundColor: "#F5F5F5 !important",
    border: "2px solid #555555",
  },
  "&:active:not(.Mui-disabled)": {
    color: "#555555",
    bgcolor: "#F5F5F5 !important",
    backgroundColor: "#F5F5F5 !important",
    backgroundImage:
      "linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.04) 100%), linear-gradient(0deg, #F5F5F5, #F5F5F5) !important",
    border: "1px solid #BBBBBB",
    boxShadow: "inset 0px 1px 4px 0px rgba(0, 0, 0, 0.5)",
  },
  "&.Mui-disabled": {
    color: "#BBBBBB !important",
    bgcolor: "#F5F5F5 !important",
    backgroundColor: "#F5F5F5 !important",
    backgroundImage: "none !important",
    border: "1px solid #E6E6E6 !important",
    boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.08)",
    opacity: "1 !important",
    cursor: "not-allowed",
  },
};

const textLinkSx = {
  border: "none",
  bgcolor: "transparent",
  p: 0,
  cursor: "pointer",
  fontFamily: FONT,
  fontWeight: 700,
  fontSize: TYPE_BUTTON.fontSize,
  lineHeight: TYPE_BUTTON.lineHeight,
  letterSpacing: 0,
  color: "#005BF0",
  flexShrink: 0,
  "&:hover": { color: "#004ACC" },
};

function TextLink({ onClick, children }) {
  return (
    <Box component="button" type="button" onClick={onClick} sx={textLinkSx}>
      {children}
    </Box>
  );
}

export default function Gate3EntryCheckPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const decision = location.state && typeof location.state === "object" ? location.state : {};

  const [bannerOpen, setBannerOpen] = useState(true);
  const [signalName, setSignalName] = useState("");
  const [signalNameLimitHit, setSignalNameLimitHit] = useState(false);
  const [planLink, setPlanLink] = useState("");
  const [savedSignal, setSavedSignal] = useState("");
  const [savedPlan, setSavedPlan] = useState("");
  const [planSavedToast, setPlanSavedToast] = useState(false);
  const [view, setView] = useState("entry");
  const [checklistReady, setChecklistReady] = useState(false);
  const [checklistValues, setChecklistValues] = useState(initialChecklistValues);
  const [checklistFiles, setChecklistFiles] = useState({ incident: null, rollback: null });
  const [checklistCustomDays, setChecklistCustomDays] = useState("0");
  const [decisionReady, setDecisionReady] = useState(false);
  const [decisionVote, setDecisionVote] = useState("conditional_go");
  const [decisionRationale, setDecisionRationale] = useState("");
  const [decisionAttested, setDecisionAttested] = useState(false);
  const [decisionException, setDecisionException] = useState({ condition: "", control: "", expiry: "" });
  const [decisionUpdated, setDecisionUpdated] = useState("");
  const allowSave = useRef(false);
  const dirty = useRef(false);
  const saveTimer = useRef(null);
  const snapshotRef = useRef(null);
  
  snapshotRef.current = {
    signalName,
    savedSignal,
    planLink,
    savedPlan,
    checklist: checklistValues,
    checklistFiles,
    checklistCustomDays,
    decisionVote,
    decisionRationale,
    decisionAttested,
    decisionCondition: decisionException.condition,
    decisionControl: decisionException.control,
    decisionExpiry: decisionException.expiry,
    decisionUpdated,
  };
  
  const [agenticTraceEnabled, setAgenticTraceEnabled] = useState(null);
  const [savedMonitoringUrl, setSavedMonitoringUrl] = useState("");
  const [savedDeploymentPlanUrl, setSavedDeploymentPlanUrl] = useState("");
  const [openCheck, setOpenCheck] = useState(null);
  const monitorsEditedRef = useRef(false);
  
  useEffect(() => {
    if (!projectId) return undefined;
    allowSave.current = false;
    dirty.current = false;
    let cancelled = false;
    monitorsEditedRef.current = false;
    setSignalName("");
    setSignalNameLimitHit(false);
    setPlanLink("");
    setSavedSignal("");
    setSavedPlan("");
    setPlanSavedToast(false);
    setOpenCheck(null);
    setChecklistReady(false);
    setChecklistValues(initialChecklistValues());
    setChecklistFiles(emptyChecklistFiles());
    setChecklistCustomDays("0");
    setDecisionReady(false);
    setDecisionVote("conditional_go");
    setDecisionRationale("");
    setDecisionAttested(false);
    setDecisionException({ condition: "", control: "", expiry: "" });
    setDecisionUpdated("");
    setAgenticTraceEnabled(null);
    setSavedMonitoringUrl("");
    setSavedDeploymentPlanUrl("");
    axios
      .get(`${API_BASE_URL}/projects/${projectId}`)
      .then((res) => {
        if (cancelled) return;
        restoreGate3(res?.data?.data?.gate3_data, {
          setSignalName,
          setPlanLink,
          setSavedSignal,
          setSavedPlan,
          setChecklistValues,
          setChecklistFiles,
          setChecklistCustomDays,
          setDecisionVote,
          setDecisionRationale,
          setDecisionAttested,
          setDecisionException,
          setDecisionUpdated,
        });
        const gate0 = res.data?.data?.gate0_data;
        const baseCode = gate0?.archetype_detail?.baseCode;
        setAgenticTraceEnabled(isSupportedAgenticArchetype(baseCode));
        const savedUrl = String(gate0?.monitoring_signals_url || "").trim();
        setSavedMonitoringUrl(savedUrl);
        setSavedDeploymentPlanUrl(String(gate0?.deployment_plan_url || "").trim());
        if (savedUrl && !monitorsEditedRef.current) {
          setChecklistValues((prev) => ({ ...prev, monitors: savedUrl }));
        }
      })
      .catch(() => {
        if (!cancelled) setAgenticTraceEnabled(false);
      })
      .finally(() => {
        setTimeout(() => {
          if (!cancelled) allowSave.current = true;
        }, 0);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  useEffect(() => {
    if (!projectId || !allowSave.current) return undefined;
    dirty.current = true;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      dirty.current = false;
      axios.put(`${API_BASE_URL}/projects/${projectId}/gate3`, snapshotRef.current).catch(() => {});
    }, 400);
    return () => clearTimeout(saveTimer.current);
  }, [
    projectId,
    signalName,
    savedSignal,
    planLink,
    savedPlan,
    checklistValues,
    checklistFiles,
    checklistCustomDays,
    decisionVote,
    decisionRationale,
    decisionAttested,
    decisionException,
    decisionUpdated,
  ]);

  useEffect(() => {
    const id = projectId;
    return () => {
      clearTimeout(saveTimer.current);
      if (!dirty.current || !id || !snapshotRef.current) return;
      dirty.current = false;
      axios.put(`${API_BASE_URL}/projects/${id}/gate3`, snapshotRef.current).catch(() => {});
    };
  }, [projectId]);

  const gate2Detail = useMemo(
    () => approvalDetail(decision.vote, decision.decidedAt),
    [decision.vote, decision.decidedAt]
  );

  const signalsPassed = Boolean(savedMonitoringUrl || savedSignal);
  const planPassed = Boolean(savedDeploymentPlanUrl || savedPlan);
  const attentionCount = (signalsPassed ? 0 : 1) + (planPassed ? 0 : 1);
  const allPassed = attentionCount === 0;

  const handleGateClick = (gateIndex) => {
    if (gateIndex === 0) navigate(`/chat/${projectId}`);
    if (gateIndex === 1) navigate(`/gate1/${projectId}`);
    if (gateIndex === 2) navigate(`/gate2/${projectId}`);
  };

  const goToGate1 = () => navigate(`/readiness/${projectId}`);
  const goBackToGate2Decision = () => navigate(`/gate2/${projectId}`, { state: { stepId: "decision" } });

  const recordedPlan = savedPlan || savedDeploymentPlanUrl;

  const toggleCheck = (id, mode) => {
    if (id === "signals") setSignalName(savedSignal);
    if (id === "plan") setPlanLink(recordedPlan);
    setOpenCheck((current) => (current?.id === id && current.mode === mode ? null : { id, mode }));
  };

  const signalPanel = openCheck?.id === "signals" ? openCheck.mode : null;
  const planPanel = openCheck?.id === "plan" ? openCheck.mode : null;
  const signalValue = signalPanel === "view" ? savedSignal : signalName;
  const planValue = planPanel === "view" ? recordedPlan : planLink;

  const showPlanSavedToast = () => {
    setPlanSavedToast(false);
    setTimeout(() => setPlanSavedToast(true), 0);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        bgcolor: "#FFFFFF",
        minWidth: 0,
        overflow: "hidden",
      }}
    >
      <IntakeGateTabsBar
        activeGate={ACTIVE_GATE}
        unlockedUpTo={ACTIVE_GATE}
        onGateClick={handleGateClick}
        showBack={false}
        showViewSummary={false}
      />

      <StickyPageHeaderWithBack
        onBack={() => {
          if (view === "outputs") setView("decision");
          else if (view === "decision") setView("flags");
          else if (view === "flags") setView("checklist");
          else if (view === "checklist") setView("entry");
          else goBackToGate2Decision();
        }}
        backAriaLabel={
          view === "outputs"
            ? "Back to decision"
            : view === "decision"
            ? "Back to flag evaluation"
            : view === "flags"
              ? "Back to readiness checklist"
              : view === "checklist"
                ? "Back to entry check"
                : "Back to decision"
        }
        title={
          view === "outputs" || view === "decision" ? "Decision" : view === "flags" ? "No-Go Flag Evaluation" : view === "checklist" ? "Readiness Checklist" : "Entry Check"
        }
        subtitle={
          view === "outputs" || view === "decision"
            ? "Record the Gate 3 verdict against the defined decision authority and evaluation outcome."
            : view === "flags"
              ? "Live evaluation of the completed checklist against Gate 3 no-go rules."
              : view === "checklist"
                ? "Certify each item that determines whether this system can be safely operated in production."
                : "Review the prerequisite checks below. All items must pass before entering Gate 3."
        }
      />

      <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: { xs: "column", md: "row" }, overflow: "hidden" }}>
        <Box
          component="nav"
          aria-label="Gate 3 workflow steps"
          sx={{
            width: { xs: "100%", md: 200, lg: 244 },
            minWidth: { md: 200, lg: 244 },
            maxHeight: { xs: 168, md: "none" },
            flexShrink: 0,
            borderRight: { xs: "none", md: "1px solid #E6E6E6" },
            borderBottom: { xs: "1px solid #E6E6E6", md: "none" },
            bgcolor: "#FFFFFF",
            overflow: "auto",
          }}
        >
          <Box component="ol" sx={{ listStyle: "none", m: 0, p: 0 }}>
            {STEPS.map((step) => {
              const isActive = step.id === view;
              const isDone =
                (view === "checklist" && step.id === "entry") ||
                (view === "flags" && (step.id === "entry" || step.id === "checklist")) ||
                (view === "decision" && (step.id === "entry" || step.id === "checklist" || step.id === "flags")) ||
                (view === "outputs" && (step.id === "entry" || step.id === "checklist" || step.id === "flags" || step.id === "decision"));
              const chipSx = isActive
                ? STEP_RAIL_IN_PROGRESS_CHIP_SX
                : isDone
                  ? STEP_RAIL_COMPLETED_CHIP_SX
                  : STEP_RAIL_PLANNED_CHIP_SX;
              return (
                <Box key={step.id} component="li" aria-current={isActive ? "step" : undefined}>
                  <Box
                    sx={{
                      ...STEP_RAIL_ROW_BASE_SX,
                      ...(isActive ? STEP_RAIL_ROW_ACTIVE_SX : STEP_RAIL_ROW_PLANNED_SX),
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0, flex: 1 }}>
                      <Box
                        aria-hidden
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          flexShrink: 0,
                          bgcolor: isActive ? "#FFFFFF" : isDone ? "#12B76A" : "transparent",
                          border: isActive || isDone ? "none" : "1.5px solid #CBD5E1",
                          boxSizing: "border-box",
                        }}
                      />
                      <TruncatedCellLine
                        text={step.label}
                        placement="right"
                        sx={{
                          ...(isActive ? STEP_RAIL_LABEL_IN_PROGRESS_SX : STEP_RAIL_LABEL_DEFAULT_SX),
                          fontSize: { xs: 12, sm: 13, lg: 14 },
                          lineHeight: "18px",
                          flex: 1,
                        }}
                      />
                    </Box>
                    <Box
                      component="span"
                      sx={{
                        ...chipSx,
                        width: { xs: "auto", lg: isActive ? 76 : isDone ? 75 : 60 },
                        minWidth: { xs: 0, lg: isActive ? 76 : isDone ? 75 : 60 },
                        maxWidth: { xs: "none", lg: isActive ? 76 : isDone ? 75 : 60 },
                        fontSize: { xs: 10, lg: 10 },
                        px: { xs: "8px", lg: 0 },
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {isActive ? "In Progress" : isDone ? "Completed" : "Planned"}
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>

        <Box
          component="main"
          aria-label={
            view === "outputs"
              ? "Gate 3 outputs"
              : view === "decision"
              ? "Gate 3 decision"
              : view === "flags"
                ? "Gate 3 flag evaluation"
                : view === "checklist"
                  ? "Gate 3 readiness checklist"
                  : "Gate 3 entry check"
          }
          sx={{ flex: 1, minWidth: 0, overflow: "auto", bgcolor: "#FFFFFF", px: { xs: 1.5, sm: 2, lg: 3 }, py: { xs: 1.5, sm: 1.75, lg: 2 } }}
        >
          {view === "outputs" ? (
            <Gate3Outputs
              verdict={decisionVote}
              updated={decisionUpdated}
              exception={decisionException}
              checklist={checklistValues}
              customDays={checklistCustomDays}
            />
          ) : view === "decision" ? (
            <Gate3Decision
              onReady={setDecisionReady}
              vote={decisionVote}
              onVoteChange={setDecisionVote}
              exception={decisionException}
              onExceptionChange={setDecisionException}
              rationale={decisionRationale}
              onRationaleChange={setDecisionRationale}
              attested={decisionAttested}
              onAttestedChange={setDecisionAttested}
              checklist={checklistValues}
              customDays={checklistCustomDays}
              traceRequired={agenticTraceEnabled === true}
            />
          ) : view === "flags" ? (
            <Gate3FlagEvaluation values={checklistValues} customDays={checklistCustomDays} />
          ) : view === "checklist" ? (
            <Gate3ReadinessChecklist
              key={projectId}
              onReady={setChecklistReady}
              values={checklistValues}
              onValuesChange={(next) => {
                if (String(next?.monitors ?? "") !== String(checklistValues.monitors ?? "")) {
                  monitorsEditedRef.current = true;
                }
                setChecklistValues(next);
              }}
              customDays={checklistCustomDays}
              onCustomDays={setChecklistCustomDays}
              mdFiles={checklistFiles}
              onMdFilesChange={setChecklistFiles}
              agenticTraceEnabled={agenticTraceEnabled}
            />
          ) : (
          <Box
            sx={{
              border: "1px solid #E6E6E6",
              borderRadius: { xs: "8px", lg: "12px" },
              p: { xs: 1.25, sm: 1.5, lg: 2 },
              display: "flex",
              flexDirection: "column",
              gap: { xs: 1.25, lg: 1.5 },
            }}
          >
            {bannerOpen && allPassed ? (
              <Box
                role="status"
                sx={{
                  ...ALERT_SHELL_SX,
                  height: { xs: "auto", lg: 64 },
                  minHeight: { lg: 64 },
                  bgcolor: "#E8FCF3",
                }}
              >
                <Box sx={{ width: { xs: 24, lg: 32 }, height: { xs: 24, lg: 32 }, flexShrink: 0, display: "flex", alignItems: "center", "& svg": { width: "100%", height: "100%", display: "block" } }}>
                  <SuccessWarningIcon />
                </Box>
                <Typography
                  sx={{
                    flex: 1,
                    fontFamily: FONT,
                    fontWeight: 700,
                    fontSize: TYPE_ALERT.fontSize,
                    lineHeight: TYPE_ALERT.lineHeight,
                    letterSpacing: 0,
                    color: "#00754E",
                  }}
                >
                  All prerequisite checks have passed. This gate is ready for entry. Review the results below to continue.
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => setBannerOpen(false)}
                  aria-label="Dismiss success alert"
                  sx={{ flexShrink: 0, color: "#707070" }}
                >
                  <CloseIcon sx={{ fontSize: { xs: 18, lg: 24 } }} />
                </IconButton>
              </Box>
            ) : null}
            {bannerOpen && !allPassed ? (
              <Box
                role="status"
                sx={{
                  ...ALERT_SHELL_SX,
                  height: { xs: "auto", lg: 74 },
                  minHeight: { lg: 74 },
                  bgcolor: "#FFF8E0",
                }}
              >
                <Box sx={{ width: { xs: 22, lg: 32 }, height: { xs: 20, lg: 32 }, flexShrink: 0, display: "flex", alignItems: "center", "& svg": { width: "100%", height: "100%", display: "block" } }}>
                  <WarningTriangleIcon />
                </Box>
                <Typography
                  sx={{
                    flex: 1,
                    fontFamily: FONT,
                    fontWeight: 400,
                    fontSize: TYPE_ALERT.fontSize,
                    lineHeight: TYPE_ALERT.lineHeight,
                    letterSpacing: 0,
                    color: "#8A5C00",
                  }}
                >
                  <Box component="span" sx={{ fontWeight: 700 }}>
                    {`${attentionCount} of 3 checks need attention`}
                  </Box>
                  {" — resolve all issues before entering Gate 3. Each issue can be fixed at the source gate or submitted here for approval. Review the options below and take action on each item to proceed."}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => setBannerOpen(false)}
                  aria-label="Dismiss warning alert"
                  sx={{ flexShrink: 0, color: "#707070" }}
                >
                  <CloseIcon sx={{ fontSize: { xs: 18, lg: 24 } }} />
                </IconButton>
              </Box>
            ) : null}

            <CheckRow
              passed
              title="Gate 2 approval recorded"
              detail={gate2Detail}
              actions={<TextLink onClick={() => toggleCheck("gate2", "view")}>View</TextLink>}
            >
              {openCheck?.id === "gate2" && openCheck.mode === "view" ? (
                <OptionBlock label="Recorded decision">
                  <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
                    <FieldLabel htmlFor="gate3-gate2-decision" required={false}>
                      Decision
                    </FieldLabel>
                    <TextLink onClick={() => navigate(`/gate2/${projectId}`)}>Edit</TextLink>
                  </Box>
                  <Box
                    component="input"
                    id="gate3-gate2-decision"
                    readOnly
                    value={gate2Detail}
                    sx={{ ...fieldSx, width: "100%" }}
                  />
                </OptionBlock>
              ) : null}
            </CheckRow>

            <CheckRow
              passed={signalsPassed}
              title={signalsPassed ? "Monitoring signals are defined" : "Monitoring signals are missing"}
              detail={
                signalsPassed
                  ? [savedMonitoringUrl, savedSignal && `1 signal submitted for approval · ${savedSignal}`]
                      .filter(Boolean)
                      .join(" · ")
                  : "No monitoring signals were configured in Gate 1."
              }
              actions={signalsPassed ? <TextLink onClick={() => toggleCheck("signals", "view")}>View</TextLink> : null}
            >
              {!savedSignal || signalPanel ? (
                <>
                  {signalsPassed ? null : (
                  <OptionBlock label="Option 1- Fix at Source">
                    <Box sx={OPTION_ROW_SX}>
                      <Typography
                        sx={{
                          fontFamily: FONT,
                          fontWeight: 400,
                          fontSize: TYPE_OPTION.fontSize,
                          lineHeight: TYPE_OPTION.lineHeight,
                          letterSpacing: 0,
                          color: "#555555",
                        }}
                      >
                        Navigate to Gate 1 to configure the missing signals directly.
                      </Typography>
                      <Button variant="outlined" disableElevation onClick={goToGate1} sx={grayBtnSx}>
                        Go to Gate 1
                      </Button>
                    </Box>
                  </OptionBlock>
                  )}
                  <OptionBlock label={signalsPassed && !signalPanel ? "Request Approval" : "Option 2- Request Approval"}>
                    <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
                      <FieldLabel htmlFor="gate3-signal-name">Signal name</FieldLabel>
                      {signalPanel === "view" ? <TextLink onClick={() => toggleCheck("signals", "edit")}>Edit</TextLink> : null}
                    </Box>
                    <Box sx={{ ...FIELD_ROW_SX, alignItems: { xs: "stretch", md: "flex-start" } }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box
                          component="input"
                          id="gate3-signal-name"
                          readOnly={signalPanel === "view"}
                          value={signalValue}
                          onChange={(e) => {
                            if (signalPanel === "view") return;
                            const cleaned = sanitizeSignalName(e.target.value);
                            if (cleaned.length > SIGNAL_NAME_MAX) {
                              setSignalName(cleaned.slice(0, SIGNAL_NAME_MAX));
                              setSignalNameLimitHit(true);
                              return;
                            }
                            setSignalName(cleaned);
                            setSignalNameLimitHit(cleaned.length >= SIGNAL_NAME_MAX);
                          }}
                          placeholder="e.g. Response latency, refusal rate, flagged-output volume"
                          sx={{
                            ...fieldSx,
                            width: "100%",
                            borderColor: signalPanel !== "view" && signalNameLimitHit ? "#D92D20" : "#D0D5DD",
                            "&::placeholder": {
                              color: "#555555",
                              opacity: 1,
                              fontFamily: FONT,
                              fontWeight: 400,
                              fontSize: TYPE_PLACEHOLDER.fontSize,
                              lineHeight: TYPE_PLACEHOLDER.lineHeight,
                              letterSpacing: 0,
                            },
                          }}
                        />
                        <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "flex-start", gap: 1, mt: 0.5 }}>
                          {signalPanel !== "view" && signalNameLimitHit ? (
                            <Typography
                              sx={{
                                fontFamily: FONT,
                                fontSize: 12,
                                lineHeight: "18px",
                                color: "#D92D20",
                                flex: 1,
                              }}
                            >
                              {SIGNAL_NAME_LIMIT_MSG}
                            </Typography>
                          ) : null}
                          <Typography
                            sx={{
                              fontFamily: FONT,
                              fontSize: 12,
                              lineHeight: "18px",
                              color: signalPanel !== "view" && signalNameLimitHit ? "#D92D20" : "#707070",
                              textAlign: "right",
                              flexShrink: 0,
                            }}
                          >
                            {signalValue.length}/{SIGNAL_NAME_MAX}
                          </Typography>
                        </Box>
                      </Box>
                      <Button
                        variant="outlined"
                        disableElevation
                        disabled={signalPanel === "view" || !isValidSignalName(signalValue)}
                        onClick={() => {
                          const next = signalValue.trim();
                          setSavedSignal(next);
                          setSignalName(next);
                          setSignalNameLimitHit(false);
                          if (signalPanel === "edit") setOpenCheck({ id: "signals", mode: "view" });
                        }}
                        sx={{ ...grayBtnSx, width: { xs: "100%", md: "auto", lg: 292 }, minWidth: { xs: 0, lg: 292 } }}
                      >
                        Submit for Enterprise AI Team approval
                      </Button>
                    </Box>
                  </OptionBlock>
                </>
              ) : null}
            </CheckRow>

            <CheckRow
              passed={planPassed}
              title={planPassed ? "Deployment plan is available" : "Deployment plan is missing"}
              detail={
                planPassed
                  ? <DeploymentPlanLink url={savedDeploymentPlanUrl || savedPlan} />
                  : "No deployment plan was added during Gate 1 setup. Navigate to Gate 1 to add it."
              }
              actions={planPassed ? <TextLink onClick={() => toggleCheck("plan", "view")}>View</TextLink> : null}
            >
              {!planPassed || planPanel ? (
                <>
                  <OptionBlock label="Option 1- Fix at Source">
                    <Box sx={OPTION_ROW_SX}>
                      <Typography
                        sx={{
                          fontFamily: FONT,
                          fontWeight: 400,
                          fontSize: TYPE_OPTION.fontSize,
                          lineHeight: TYPE_OPTION.lineHeight,
                          letterSpacing: 0,
                          color: "#555555",
                        }}
                      >
                        Navigate to Gate 1 to add the missing deployment plan link.
                      </Typography>
                      <Button variant="outlined" disableElevation onClick={goToGate1} sx={grayBtnSx}>
                        Go to Gate 1
                      </Button>
                    </Box>
                  </OptionBlock>
                  <OptionBlock label="Option 2- Request Approval">
                    <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
                      <FieldLabel htmlFor="gate3-plan-link">Plan link</FieldLabel>
                      {planPanel === "view" ? <TextLink onClick={() => toggleCheck("plan", "edit")}>Edit</TextLink> : null}
                    </Box>
                    <Box sx={FIELD_ROW_SX}>
                      <Box
                        component="input"
                        id="gate3-plan-link"
                        readOnly={planPanel === "view"}
                        value={planValue}
                        onChange={(e) => setPlanLink(e.target.value)}
                        placeholder="e.g. https://confluence.example.com/deployment-plan"
                        sx={{
                          ...fieldSx,
                          "&::placeholder": {
                            color: "#555555",
                            opacity: 1,
                            fontFamily: FONT,
                            fontWeight: 400,
                            fontSize: TYPE_PLACEHOLDER.fontSize,
                            lineHeight: TYPE_PLACEHOLDER.lineHeight,
                            letterSpacing: 0,
                          },
                        }}
                      />
                      <Button
                        variant="contained"
                        disableElevation
                        disabled={planPanel === "view" || !isValidPlanLink(planValue)}
                        onClick={() => {
                          const next = planValue.trim();
                          clearTimeout(saveTimer.current);
                          dirty.current = false;
                          axios
                            .put(`${API_BASE_URL}/projects/${projectId}/gate3`, {
                              ...snapshotRef.current,
                              planLink: next,
                              savedPlan: next,
                            })
                            .then((res) => {
                              if (!res.data?.success) return;
                              setPlanLink(next);
                              setSavedPlan(next);
                              if (planPanel === "edit") setOpenCheck({ id: "plan", mode: "view" });
                              showPlanSavedToast();
                            })
                            .catch(() => {});
                        }}
                        sx={{
                          ...PRIMARY_BTN_SX,
                          ...TYPE_BUTTON,
                          height: { xs: 36, sm: 38, lg: 40 },
                          minHeight: { xs: 36, lg: 40 },
                          width: { xs: "100%", sm: "auto" },
                          minWidth: { xs: 0, sm: 96 },
                          px: { xs: "12px", lg: "16px" },
                        }}
                      >
                        Save Link
                      </Button>
                    </Box>
                  </OptionBlock>
                </>
              ) : null}
            </CheckRow>
          </Box>
          )}
        </Box>
      </Box>

      <Box
        component="footer"
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          gap: { xs: 1.5, sm: 2 },
          height: 56,
          boxSizing: "border-box",
          px: { xs: 1.5, sm: 2, lg: 3 },
          py: 0,
          borderTop: "1px solid #E6E6E6",
          bgcolor: "#FFFFFF",
          boxShadow: "0px -1px 8px 0px #2122260A",
          flexShrink: 0,
        }}
      >
        <Button
          variant="outlined"
          disableElevation
          onClick={() => {
            if (view === "outputs") setView("decision");
            else if (view === "decision") setView("flags");
            else if (view === "flags") setView("checklist");
            else if (view === "checklist") setView("entry");
            else goBackToGate2Decision();
          }}
          sx={{
            ...SCOPE_CANCEL_BTN_SX,
            ...TYPE_BUTTON,
            width: { xs: "100%", sm: "auto" },
            height: { xs: 36, lg: 40 },
            minHeight: { xs: 36, lg: 40 },
            px: { xs: "12px", lg: "16px" },
          }}
        >
          Back
        </Button>
        <Button
          variant="contained"
          disableElevation
          disabled={(view === "entry" && !allPassed) || (view === "checklist" && !checklistReady) || (view === "decision" && !decisionReady)}
          onClick={() => {
            if (view === "entry") setView("checklist");
            else if (view === "checklist") setView("flags");
            else if (view === "flags") setView("decision");
            else if (view === "decision") {
              const now = new Date();
              const month = String(now.getMonth() + 1).padStart(2, "0");
              const day = String(now.getDate()).padStart(2, "0");
              setDecisionUpdated(`${now.getFullYear()}-${month}-${day}`);
              setView("outputs");
            }
            else if (view === "outputs") navigate("/projects");
          }}
          sx={{
            ...PRIMARY_BTN_SX,
            ...TYPE_BUTTON,
            width: { xs: "100%", sm: "auto" },
            height: { xs: 36, lg: 40 },
            minHeight: { xs: 36, lg: 40 },
            px: { xs: "12px", lg: "16px" },
          }}
        >
          {view === "outputs"
            ? "Return to Projects"
            : view === "decision"
            ? "Record Decision"
            : view === "flags"
              ? "Continue to Decision"
              : view === "checklist"
                ? "Continue to Evaluation"
                : "Continue to Checklist"}
        </Button>
      </Box>

      <AppToast
        open={planSavedToast}
        title="Deployment plan saved successfully"
        onClose={() => setPlanSavedToast(false)}
      />
    </Box>
  );
}
